import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useEvents, EVENT_TYPE_COLORS, formatEventDay, formatEventTime, formatEventDate } from '../../hooks/useEvents';
import { Search, Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Star, MessageCircle, PlusCircle, ArrowRight, Sun, MessageSquareText } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import SoonTag from '../common/SoonTag';
import VideoPreviewCard from '../common/VideoPreviewCard';
import EventListItem from '../common/EventListItem';
import { mentors } from '../../data/mockData';
import { useBookshelf } from '../../hooks/useBookshelf';
import { groupByShelf } from '../../lib/bookshelf';
import BookshelfCard from '../common/BookshelfCard';
import FeedbackModal from '../common/FeedbackModal';
import { RELAX_SESSIONS } from '../../lib/relaxSessions';
import RelaxAudioCard from '../common/RelaxAudioCard';
import { PaperBall } from '../common/CrumpledPaper';
import { useVideos } from '../../hooks/useVideos';
import { VIDEO_FILTER_CATEGORIES } from '../../lib/videoLibrary';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

// Explore's five areas, grouped by whether the thing behind the card actually
// exists. `status` is the single source of truth for the badge, the action
// wording and which row a card lands in — so a section cannot look live by
// accident, and shipping a backend means changing 'preview' to 'live' here
// rather than hunting through JSX.
//
// ⚠️ 'preview' means "the page is reachable and shows sample content", NOT
// "coming soon, don't click". All five destinations stay navigable — the
// mentorship page in particular is demoed live at webinars, so its access must
// never be removed. What the copy must never do is imply working matching,
// booking or messaging.
const buildSections = (eventCount, bookCount) => [
  {
    id: 'videos',
    status: 'live',
    title: 'Video Library',
    description: 'Practical tips and stories from the DSP community.',
    cta: 'Browse videos',
    icon: '/assets/explore/videos.png',
  },
  {
    id: 'events',
    status: 'live',
    title: 'Event Calendar',
    description: 'Upcoming webinars, workshops, and meetups.',
    count: eventCount === null ? '' : `${eventCount} upcoming`,
    cta: 'View events',
    icon: '/assets/explore/events.png',
  },
  {
    id: 'bookshelf',
    status: 'live',
    title: 'Bookshelf',
    description: 'Guides, courses, and reading for your work.',
    count: bookCount === null ? '' : `${bookCount} ${bookCount === 1 ? 'resource' : 'resources'}`,
    cta: 'Open the shelf',
    icon: '/assets/explore/bookshelf.png',
  },
  {
    id: 'relax',
    status: 'live',
    title: 'Relax',
    description: 'Guided audio, a gratitude journal, and a place to let off steam.',
    cta: 'Take a moment',
    icon: '/assets/explore/relax.png',
  },
  {
    id: 'mentorship',
    status: 'preview',
    title: 'Virtual Mentorship',
    description: 'Explore support for your professional growth.',
    // "Preview", not "Soon": this page is opened in front of an audience at
    // webinars, and "Soon" on something you can click right now reads as
    // "don't bother".
    badge: 'Preview',
    cta: 'Preview mentorship',
    icon: '/assets/explore/mentorship.png',
  },
];

// One card, one semantic link. The action reads as a button but is a <span> —
// the whole card is already the anchor, and an <a> inside an <a> is invalid
// HTML. Status is carried by a badge and by the action's wording, not by fading
// the card out: a preview area still has to look worth opening.
const SectionCard = ({ section }) => {
  const preview = section.status === 'preview';
  return (
    <Link
      to={`/explore/${section.id}`}
      className="group flex items-start gap-3.5 rounded-2xl p-4 md:p-5 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
      style={cardStyle}
    >
      {/* Horizontal composition. Stacked, the icon pushed the text down and the
          action sat at the bottom of a tall card with dead space between; side
          by side the card is content-height. No fixed or min height anywhere —
          the grid uses items-start so each card sizes to its own content. */}
      <img
        src={section.icon}
        alt=""
        className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 object-contain"
        loading="lazy"
      />

      <div className="min-w-0 flex-1">
        {/* Badge sits beside the title and is allowed to wrap under it rather
            than squeezing a long name. */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-bold text-base text-primary leading-snug">{section.title}</h3>
          {preview ? <SoonTag label={section.badge} /> : section.count ? (
            <span className="text-xs flex-shrink-0 mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {section.count}
            </span>
          ) : null}
        </div>

        <p className="text-sm mt-1 leading-snug" style={{ color: 'var(--text-tertiary)' }}>
          {section.description}
        </p>

        {/* Directly under the description — not mt-auto. Pushing it to the
            bottom is what made these cards tall and hollow. */}
        <span
          className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold transition-opacity group-hover:opacity-80"
          style={{ color: 'var(--brand-primary)' }}
        >
          {section.cta}
          <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  );
};

const ExploreScreen = () => {
  const { section } = useParams();
  const { events, loading: eventsLoading } = useEvents({ limit: 50 });
  const { items: bookshelf, loading: bookshelfLoading, error: bookshelfError } = useBookshelf();
  const sections = buildSections(
    eventsLoading ? null : events.length,
    bookshelfLoading || bookshelfError ? null : bookshelf.length,
  );
  const navigate = useNavigate();
  const { userData } = useUser();
  const { videos, loading: videosLoading } = useVideos();
  const [videoFilter, setVideoFilter] = useState('All');
  const [showMentorFeedback, setShowMentorFeedback] = useState(false);

  // The hub's search is deliberately video-only for now — placeholder and
  // routing both say so. Broadening it is a later piece of work; until then the
  // narrow scope is the honest thing to advertise. ?q= carries the term across
  // the route change so it survives a reload or a shared link.
  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [hubQuery, setHubQuery] = useState(urlQuery);

  useEffect(() => { setSearchQuery(urlQuery); }, [urlQuery]);

  // ── Discovery strip ──────────────────────────────────────────────
  // Both sources are ALREADY fetched above for the section views, so this adds
  // no request.
  //
  // ⚠️ useVideos() returns a daily per-user rotation of
  // [...curated, ...submissions] — not chronological. The filter is a guard in
  // case mock rows ever come back: they set neither isCurated nor isSubmission,
  // and must never be listed under "Latest videos" as though published.
  //
  // Sorting needs publishedAt (raw), not timestamp (display text like
  // "Sep 2026", which cannot be ordered).
  const latestVideos = useMemo(() => (
    videos
      .filter(v => v.isCurated || v.isSubmission)
      .slice()
      .sort((a, b) => new Date(b.publishedAt ?? 0) - new Date(a.publishedAt ?? 0))
      .slice(0, 2)
  ), [videos]);

  // get_upcoming_events already excludes anything finished and orders by start
  // (031); sorting here keeps it correct if that ever changes.
  const nextEvents = useMemo(() => (
    [...events].sort((a, b) => a.startsAt - b.startsAt).slice(0, 2)
  ), [events]);


  const submitHubSearch = (e) => {
    e.preventDefault();
    const q = hubQuery.trim();
    navigate(q ? `/explore/videos?q=${encodeURIComponent(q)}` : '/explore/videos');
  };

  const BackButton = ({ label }) => (
    <button
      onClick={() => navigate('/explore')}
      className="flex items-center gap-1 mb-3 text-sm transition-colors text-white/70 hover:text-white"
    >
      <ChevronLeft size={18} />
      {label || 'Back to Explore'}
    </button>
  );

  // The badge comes from the same `sections` entry as the hub card, so a
  // sub-page can't disagree with the card that led to it. It used to take a
  // bare `soon` flag, which printed "Soon" on the mentorship page while its
  // hub card correctly said "Preview".
  const SectionHeader = ({ title, children, sectionId }) => {
    const section = sections.find((s) => s.id === sectionId);
    const preview = section?.status === 'preview';
    return (
    <>
      {/* Mobile: sticky brand-color header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <BackButton />
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white tracking-tight">{title}</h1>
            {preview && <SoonTag label={section.badge} onDark />}
          </div>
          {children}
        </div>
      </div>
      {/* Desktop: plain content heading — TopNav "Explore" serves as back navigation */}
      <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
          {preview && <SoonTag label={section.badge} />}
        </div>
        {children}
      </div>
    </>
    );
  };


  // --- Video Library ---
  if (section === 'videos') {

    const parseViews = (v) => {
      if (!v.views) return 0;
      const s = String(v.views).toUpperCase();
      if (s.endsWith('K')) return parseFloat(s) * 1000;
      if (s.endsWith('M')) return parseFloat(s) * 1_000_000;
      return parseFloat(s) || 0;
    };
    const trendingVideos = [...videos].sort((a, b) => parseViews(b) - parseViews(a)).slice(0, 4);

    const filtered = videos
      .filter(v => videoFilter === 'All' || v.category === videoFilter)
      .filter(v => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return v.title.toLowerCase().includes(q) || (v.creator || '').toLowerCase().includes(q);
      });

    const isFiltered = videoFilter !== 'All' || searchQuery.trim();

    const chipStyle = (active) => ({
      backgroundColor: active ? 'var(--brand-primary)' : 'var(--input-bg)',
      color: active ? '#fff' : 'var(--text-secondary)',
      border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--card-border)'),
    });

    return (
      <div className="min-h-screen pb-nav">

        {/* Mobile: compact sticky header */}
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <BackButton />
            <h1 className="text-xl font-bold text-white tracking-tight">Video Library</h1>
          </div>
        </div>

        {/* Mobile: search */}
        <div className="md:hidden px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5" size={15} style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search videos..."
              className="w-full pl-8 pr-3 py-2 rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
            />
          </div>
        </div>

        {/* Mobile: horizontal scroll chips */}
        <div className="md:hidden px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
          {VIDEO_FILTER_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setVideoFilter(cat)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all active:scale-[0.98]"
              style={chipStyle(videoFilter === cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Mobile: submit button */}
        {userData?.verified && (
          <div className="md:hidden px-4 pb-3 flex justify-end">
            <button
              onClick={() => navigate('/submit-video')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)', color: '#fff' }}
            >
              <PlusCircle size={14} />
              Submit a Video
            </button>
          </div>
        )}

        <div className="max-w-4xl mx-auto px-4 md:px-6">

          {/* Desktop: heading + search + submit */}
          <div className="hidden md:flex pt-6 pb-3 items-center gap-4">
            <h2 className="text-2xl font-bold flex-shrink-0" style={{ color: 'var(--text-primary)' }}>Video Library</h2>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-2.5" size={15} style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search videos..."
                className="w-full pl-8 pr-3 py-2 rounded-lg text-sm focus:outline-none"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
              />
            </div>
            {userData?.verified && (
              <button
                onClick={() => navigate('/submit-video')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                style={{ backgroundColor: 'var(--brand-primary)', color: '#fff' }}
              >
                <PlusCircle size={15} />
                Submit a Video
              </button>
            )}
          </div>

          {/* Desktop: category chips */}
          <div className="hidden md:flex pb-4 flex-wrap gap-2">
            {VIDEO_FILTER_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setVideoFilter(cat)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all active:scale-[0.98]"
                style={chipStyle(videoFilter === cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Trending card — hidden when actively filtering/searching */}
          {!isFiltered && (
            <div className="rounded-2xl overflow-hidden mb-5" style={cardStyle}>
              <div className="px-4 pt-4 pb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-primary">Trending</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `rgba(var(--brand-accent-rgb), 0.12)`, color: 'var(--brand-accent-text)' }}>This week</span>
              </div>
              <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {trendingVideos.map(video => <VideoPreviewCard key={video.id} video={video} />)}
              </div>
            </div>
          )}

          {/* Video grid */}
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No videos match your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pb-4">
              {filtered.map(video => <VideoPreviewCard key={video.id} video={video} />)}
            </div>
          )}

        </div>
      </div>
    );
  }

  // --- Event Calendar ---
  if (section === 'events') {
    return (
      <div className="min-h-screen pb-nav">
        <SectionHeader title="Event Calendar" />

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
          {eventsLoading && (
            <div className="py-12 text-center text-sm text-tertiary">Loading events…</div>
          )}

          {!eventsLoading && events.length === 0 && (
            <div className="rounded-xl p-8 flex flex-col items-center gap-2 text-center" style={cardStyle}>
              <Calendar size={32} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
              <p className="text-sm font-medium text-primary">No upcoming events</p>
              <p className="text-xs text-tertiary opacity-70">Check back soon — new events are added regularly.</p>
            </div>
          )}

          {!eventsLoading && events.map(event => {
            const { month, day } = formatEventDay(event.startsAt);
            const where = event.isVirtual ? (event.location || 'Virtual') : event.location;
            // ends_at is fetched and normalized but was never rendered, so every
            // event showed a start with no end — you couldn't tell a 30-minute
            // briefing from an all-day session. A multi-day event needs the date
            // too, or "2:00 PM – 4:00 PM" would misdescribe it.
            const sameDay = event.endsAt
              && event.endsAt.toDateString() === event.startsAt.toDateString();
            const timeRange = !event.endsAt
              ? formatEventTime(event.startsAt)
              : sameDay
              ? `${formatEventTime(event.startsAt)} – ${formatEventTime(event.endsAt)}`
              : `${formatEventTime(event.startsAt)} – ${formatEventDate(event.endsAt)}, ${formatEventTime(event.endsAt)}`;
            const Wrapper = event.registrationUrl ? 'a' : 'div';
            const wrapperProps = event.registrationUrl
              ? { href: event.registrationUrl, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <Wrapper
                key={event.id}
                {...wrapperProps}
                className="rounded-xl p-4 flex gap-4 items-start transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer"
                style={cardStyle}
              >
                <div
                  className="flex flex-col items-center justify-center rounded-lg px-3 py-2 min-w-[52px]"
                  style={{ backgroundColor: EVENT_TYPE_COLORS[event.type] || 'var(--brand-primary)', color: 'white' }}
                >
                  <span className="text-[10px] font-bold leading-none">{month}</span>
                  <span className="text-xl font-bold leading-tight">{day}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-primary leading-tight">{event.title}</h3>
                  {event.host && (
                    <p className="text-xs text-tertiary mt-0.5">Hosted by {event.host}</p>
                  )}
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-tertiary">
                    <Clock size={12} className="flex-shrink-0" />
                    <span>{timeRange}</span>
                  </div>
                  {where && (
                    <div className="flex items-start gap-1 mt-1 text-xs text-tertiary">
                      <MapPin size={12} className="flex-shrink-0 mt-0.5" />
                      <span className="break-words">{where}</span>
                    </div>
                  )}
                  {/* Full text, not line-clamp-2. This is the calendar — there
                      is no event detail route to "read more" into, so a clamp
                      here truncated the description with nowhere to go.
                      whitespace-pre-wrap preserves the paragraph breaks an admin
                      typed (PostDetailScreen's precedent for stored prose). */}
                  {event.description && (
                    <p className="text-xs text-secondary mt-2 leading-relaxed whitespace-pre-wrap break-words">
                      {event.description}
                    </p>
                  )}
                  {event.registrationUrl && (
                    <span
                      className="inline-block mt-2.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    >
                      Register
                    </span>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    );
  }

  // --- Bookshelf ---
  // Real resources since 059, managed at /admin/bookshelf. Every item links out
  // to the publisher. Grouped into topic shelves (user's call on the mockup).
  if (section === 'bookshelf') {
    const shelves = groupByShelf(bookshelf);
    return (
      <div className="min-h-screen pb-nav">
        <SectionHeader title="Bookshelf" sectionId="bookshelf" />

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-6">
          <p className="text-sm leading-snug" style={{ color: 'var(--text-tertiary)' }}>
            Guides, programs and reading picked for direct support work. Each one opens on the publisher's website.
          </p>

          {bookshelfLoading && (
            <div className="py-12 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading the shelf…</div>
          )}

          {!bookshelfLoading && bookshelfError && (
            <div className="rounded-xl p-4 text-sm" style={cardStyle}>
              The bookshelf didn't load. Check your connection and open it again.
            </div>
          )}

          {!bookshelfLoading && !bookshelfError && shelves.length === 0 && (
            <div className="rounded-xl p-6 text-center text-sm" style={{ ...cardStyle, color: 'var(--text-tertiary)' }}>
              Nothing on the shelf yet. Resources are on the way.
            </div>
          )}

          {shelves.map(shelf => (
            <section key={shelf.name} className="space-y-2.5">
              <h2 className="text-[15px] font-bold" style={{ color: 'var(--text-primary)' }}>{shelf.name}</h2>
              <div className="space-y-3">
                {shelf.items.map(item => <BookshelfCard key={item.id} item={item} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  // --- Virtual Mentorship ---
  if (section === 'mentorship') {
    return (
      <div className="min-h-screen pb-nav">
        <SectionHeader title="Virtual Mentorship" sectionId="mentorship" />

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
          {/* ⚠️ Never describe mentorship as live (CLAUDE.md, Product honesty).
              People who join after launch didn't hear "work in progress" at a
              webinar, so the page says it plainly (user's wording, 2026-10-29).
              "Tell us" is an in-app form, not an email: members are signed in
              and people are less likely to email (063). */}
          <div className="rounded-xl p-4" style={{ ...cardStyle, borderColor: '#9B7EC8' }}>
            {/* Staff wording, interim (2026-10-30). */}
            <h2 className="font-bold text-[15px] text-primary mb-1">Mentorship is on the way</h2>
            <p className="text-sm text-secondary leading-relaxed mb-2">
              Mentorship matters, and we want to get it right. The program is still being built,
              and we&rsquo;ll soon be looking for experienced DSPs who&rsquo;d like to mentor.
            </p>
            <p className="text-sm text-secondary leading-relaxed mb-3">
              Below is an example of how it may work. The mentors shown are not real people.
            </p>
            <button
              onClick={() => setShowMentorFeedback(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              <MessageSquareText size={16} />
              Tell us what you&rsquo;d want from a mentor
            </button>
          </div>
          {showMentorFeedback && (
            <FeedbackModal topic="mentorship" onClose={() => setShowMentorFeedback(false)} />
          )}

          {/* Pathway Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-xl p-4"
              style={{ ...cardStyle, borderColor: '#9B7EC8' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: '#9B7EC8' }}
              >
                <Star size={20} color="white" />
              </div>
              <h3 className="font-bold text-sm text-primary mb-1">Become a Mentor</h3>
              <p className="text-xs text-tertiary leading-snug">
                Share your experience and help the next generation of DSPs thrive.
              </p>
            </div>
            <div
              className="rounded-xl p-4"
              style={{ ...cardStyle, borderColor: '#4DAAAA' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: '#4DAAAA' }}
              >
                <Search size={20} color="white" />
              </div>
              <h3 className="font-bold text-sm text-primary mb-1">Find a Mentor</h3>
              <p className="text-xs text-tertiary leading-snug">
                Get paired with someone who understands the work and can guide you.
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="rounded-xl p-4" style={cardStyle}>
            <h3 className="font-bold text-sm text-primary mb-3">How It Works</h3>
            <div className="space-y-3">
              {[
                { step: '1', text: 'Choose your path — mentor or mentee' },
                { step: '2', text: 'Tell us about your experience and goals' },
                { step: '3', text: 'Get matched based on specialty and availability' },
                { step: '4', text: 'Connect via chat or video on your schedule' },
              ].map(({ step, text }) => (
                <div key={step} className="flex items-start gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: '#9B7EC8' }}
                  >
                    {step}
                  </div>
                  <p className="text-sm text-secondary">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Featured Mentors */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-tertiary uppercase tracking-wide">
              Example mentors
            </h3>
            <div className="space-y-3">
              {mentors.map(mentor => (
                <div
                  key={mentor.id}
                  className="rounded-xl p-4"
                  style={cardStyle}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #9B7EC8, #7C5DAF)' }}
                    >
                      {mentor.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm text-primary">{mentor.name}</h4>
                      <p className="text-xs text-tertiary">{mentor.role}</p>
                    </div>
                    {/* Was a mentee count: next to "vetted", a named card with
                        "3 mentees" reads as a real, active mentor. */}
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                      style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-tertiary)', border: '1px solid var(--pill-border)' }}
                    >
                      Example
                    </span>
                  </div>

                  <p className="text-sm text-secondary leading-relaxed mb-3">
                    "{mentor.bio}"
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-tertiary">
                      <span className="font-medium" style={{ color: '#9B7EC8' }}>{mentor.specialty}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--card-border)' }}>
                    <div className="flex items-center gap-1.5 text-xs text-tertiary">
                      <Clock size={12} />
                      <span>{mentor.availability}</span>
                    </div>
                    {/* Was: requireVerified() and then nothing — it passed the
                        gate and silently did no work. */}
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-tertiary)', border: '1px solid var(--pill-border)' }}
                    >
                      <MessageCircle size={14} />
                      Connect
                      <SoonTag />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Relax ---
  // Real content: guided audio, Three Good Things and the Rant Room. The old
  // sample list (body scan, box breathing…) was removed rather than mixed in —
  // real and fake sessions side by side would leave members unsure which play.
  //
  // Positive first, Rant Room last (feedback, 2026-10-27: with the Rant Room
  // on top the area read as negative).
  const relaxLink = ({ to, icon, tint, title, blurb }) => (
    <Link
      to={to}
      className="flex items-center gap-3.5 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
      style={cardStyle}
    >
      <span
        className="w-12 h-12 rounded-[14px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: tint }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-[15px] leading-snug" style={{ color: 'var(--text-primary)' }}>{title}</span>
        <span className="block text-sm mt-0.5 leading-snug" style={{ color: 'var(--text-tertiary)' }}>{blurb}</span>
      </span>
      <ChevronRight size={18} className="flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
    </Link>
  );

  if (section === 'relax') {
    return (
      <div className="min-h-screen pb-nav">
        <SectionHeader title="Relax" sectionId="relax" />

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
            A moment for yourself
          </h2>
          {RELAX_SESSIONS.map((s) => <RelaxAudioCard key={s.id} session={s} />)}
          <p className="text-xs pt-1" style={{ color: 'var(--text-tertiary)' }}>More sessions are on the way.</p>

          <h2 className="text-base font-bold pt-4" style={{ color: 'var(--text-primary)' }}>
            Notice the good
          </h2>
          {relaxLink({
            to: '/explore/relax/gratitude',
            icon: <Sun size={26} style={{ color: '#A8761C' }} />,
            tint: 'rgba(212,160,60,0.16)',
            title: 'Three Good Things',
            blurb: 'Write down what went right today. Saved on this device only.',
          })}

          <h2 className="text-base font-bold pt-4" style={{ color: 'var(--text-primary)' }}>
            Let off steam
          </h2>
          {relaxLink({
            to: '/explore/relax/rant-room',
            icon: <PaperBall size={30} />,
            tint: 'rgba(224,122,95,0.14)',
            title: 'Rant Room',
            blurb: 'Write out a rough shift, then crumple it up. Nothing is saved.',
          })}
        </div>
      </div>
    );
  }

  // --- Hub View (default) ---
  // A navigational directory: five destinations grouped by whether what sits
  // behind them exists yet. Centred in the app shell, which is the single
  // desktop width cap — this screen adds no max-width of its own.
  const live = sections.filter(s => s.status === 'live');
  const preview = sections.filter(s => s.status === 'preview');

  const searchField = (dark) => (
    <form onSubmit={submitHubSearch} className="relative">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        size={16}
        style={{ color: dark ? 'rgba(255,255,255,0.6)' : 'var(--text-tertiary)' }}
      />
      <input
        type="search"
        value={hubQuery}
        onChange={e => setHubQuery(e.target.value)}
        placeholder="Search videos…"
        aria-label="Search videos"
        className={`w-full pl-9 pr-4 py-2 rounded-lg text-sm focus:outline-none ${
          dark ? 'placeholder:text-white/60' : 'placeholder:text-[color:var(--text-tertiary)]'
        }`}
        style={dark ? {
          backgroundColor: 'rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: 'white',
        } : {
          backgroundColor: 'var(--input-bg)',
          border: '1px solid var(--input-border)',
          color: 'var(--text-primary)',
        }}
      />
    </form>
  );

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile header — search sits directly beneath it */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="px-4 py-2">
          <h1 className="text-2xl font-bold text-white tracking-tight mb-2.5">Explore</h1>
          {searchField(true)}
        </div>
      </div>

      <div className="px-4 md:px-6 py-5 md:py-8">
        {/* Desktop intro, then search, then the cards */}
        <div className="hidden md:block mb-5">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Explore</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Discover videos, events, resources, and time for yourself.
          </p>
          <div className="mt-4 max-w-md">{searchField(false)}</div>
        </div>

        {/* Available now. items-start so a card never stretches to match a
            taller neighbour and grow hollow space inside itself. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          {live.map(section => <SectionCard key={section.id} section={section} />)}
        </div>

        {/* Preview. ⚠️ Reachable, not disabled — mentorship especially, which is
            opened in front of an audience at webinars. Same two-column grid as
            the live row, so the lone card lines up with the cards above. */}
        <div className="mt-8">
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
            Preview what's coming
          </h2>
          <p className="text-sm mt-1 mb-4" style={{ color: 'var(--text-tertiary)' }}>
            A first look at an area still being built, shown with sample content.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            {preview.map(section => <SectionCard key={section.id} section={section} />)}
          </div>
        </div>

        {/* Discovery — real content, openable from here. Deliberately BELOW all
            five destination cards so mentorship stays easy to find, and
            deliberately finite: two videos, up to two events, no carousel or
            pagination.

            The videos carry the visual weight — two 16:9 previews side by side,
            no wrapper card around them, because nesting cards inside a card
            made this read as a sidebar widget. Events stay a compact list in
            their own card beside it. On mobile the whole strip stacks: videos
            first, then events, both below the navigation. */}
        {(latestVideos.length > 0 || nextEvents.length > 0 || videosLoading || eventsLoading) && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 lg:gap-6 items-start">
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-base font-bold text-primary">Latest videos</h2>
                <Link
                  to="/explore/videos"
                  className="text-xs font-semibold flex-shrink-0 hover:underline"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  View all videos
                </Link>
              </div>
              {videosLoading ? (
                <p className="text-xs text-tertiary py-2">Loading videos…</p>
              ) : latestVideos.length === 0 ? (
                <p className="text-xs text-tertiary py-2">No videos published yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  {latestVideos.map(v => <VideoPreviewCard key={v.id} video={v} />)}
                </div>
              )}
            </section>

            <section className="rounded-2xl p-4 md:p-5" style={cardStyle}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-base font-bold text-primary">Upcoming events</h2>
                <Link
                  to="/explore/events"
                  className="text-xs font-semibold flex-shrink-0 hover:underline"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  View all events
                </Link>
              </div>
              {eventsLoading ? (
                <p className="text-xs text-tertiary py-2">Loading events…</p>
              ) : nextEvents.length === 0 ? (
                <p className="text-xs text-tertiary py-2">No upcoming events right now.</p>
              ) : (
                <div className="space-y-3.5">
                  {nextEvents.map(e => <EventListItem key={e.id} event={e} />)}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreScreen;
